// =============================================================================
// AcceptableUse.js, /acceptable-use (Acceptable Use Policy)
//
// Phase 38.7, New legal page.
//
// Tone: calm and short. We list a small number of clearly-out-of-bounds
// behaviours and reserve the right to suspend / terminate accordingly.
// =============================================================================
import React from 'react';
import LegalPage from '@/components/LegalPage';
import usePageTitle from '@/hooks/usePageTitle';

export default function AcceptableUse() {
  usePageTitle('Acceptable Use Policy');
  const sections = [
    {
      heading: 'The principle',
      body: '2120 is a tool for individual financial clarity. We trust adults to bring their real financial life to the product and treat it with the calm it deserves. This policy describes the small number of behaviours that fall outside that trust.',
    },
    {
      heading: 'What you must not do',
      body: 'You must not, and must not allow anyone using your account to:',
      list: [
        'Use 2120 to store, share, or process content that is unlawful, infringing, defamatory, harassing, hateful, or sexually exploitative of any person.',
        'Use 2120 to plan, enable, or conceal fraud, money laundering, tax evasion, sanctions evasion, or financing of any other illegal activity.',
        'Probe, scan, or test the security of the Service except under a written agreement with us, or attempt to bypass authentication, rate limits, plan gating, or other technical controls.',
        'Reverse engineer, decompile, scrape, or attempt to extract source code or model weights from the Service, or train any machine-learning model on the Service’s output without our written permission.',
        'Use bots, scripts, or other automated means to access the Service in a way that imposes an unreasonable load or that circumvents intended usage limits.',
        'Impersonate another person, sign up using fake identity details, or create multiple accounts to evade our policies or plan limits.',
        'Share your account credentials with anyone outside your authorised Summit workspace, or use a single paid seat to provide services to third parties as if you were us.',
      ],
    },
    {
      heading: 'AI usage',
      body: 'The AI features in 2120 are for personal financial reflection only. You must not submit prompts or content designed to extract harmful instructions, generate disallowed content, or produce output that violates the providers’ own usage policies (Anthropic, Google, OpenAI). We rely on the providers’ abuse detection in addition to our own.',
    },
    {
      heading: 'How we enforce this',
      body: 'If we reasonably believe an account has violated this policy, we may (in order of severity, depending on the situation): warn you, restrict specific features, suspend the account while we investigate, or terminate the account. For serious or repeated violations, especially fraud, child-safety violations, or attacks on the Service, we will terminate immediately and may notify the appropriate authorities. We do not refund unused subscription time when terminating for cause; see the Refund Policy.',
    },
    {
      heading: 'Reporting abuse',
      body: 'If you believe someone is using 2120 to harm you or another person, for example, a Summit workspace partner who has become abusive, or a fraudulent invoice, please email privacy@2120.money. We treat reports confidentially and respond within two business days.',
    },
    {
      heading: 'Changes',
      body: 'We may update this policy as we learn from real-world use. The “Last updated” date at the top of this page reflects the most recent revision.',
    },
  ];

  return (
    <LegalPage
      title="Acceptable Use Policy"
      subtitle="A short list of things that are clearly out of bounds, and what happens if they occur."
      lastUpdated="May 31, 2026"
      sections={sections}
    />
  );
}
